import React, { useState } from "react";

import { AlertCircle, CheckCircle2, FileText, Upload } from "lucide-react";
import { Loader2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useImportAccountMappings,
  useParseCSV,
  useValidateMappings,
} from "~/hooks/contabilidad/useAccountMappingImport";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import type { AccountMappingCSVRow } from "~/types/contabilidad";

export default function ImportMappingsPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [companyId, setCompanyId] = useState<string>("");
  const [templateName, setTemplateName] = useState<string>("");
  const [parsedRows, setParsedRows] = useState<AccountMappingCSVRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    Array<{ row: number; error: string }>
  >([]);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    imported: number;
    skipped: number;
  } | null>(null);

  const { data: companies = [] } = useCompanies();
  const parseCSVMutation = useParseCSV();
  const validateMutation = useValidateMappings();
  const importMutation = useImportAccountMappings();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".csv")) {
        alert("Please select a CSV file");
        return;
      }
      setSelectedFile(file);
      setParsedRows([]);
      setValidationErrors([]);
      setImportResult(null);
    }
  };

  const handleParse = async () => {
    if (!selectedFile) return;

    try {
      const rows = await parseCSVMutation.mutateAsync(selectedFile);
      setParsedRows(rows);

      // Automatically validate if company is selected
      if (companyId) {
        const result = await validateMutation.mutateAsync({
          rows,
          companyId,
        });
        setValidationErrors(result.errors);
      }
    } catch (error) {
      console.error("Parse error:", error);
    }
  };

  const handleValidate = async () => {
    if (!parsedRows.length || !companyId) return;

    try {
      const result = await validateMutation.mutateAsync({
        rows: parsedRows,
        companyId,
      });
      setValidationErrors(result.errors);
    } catch (error) {
      console.error("Validation error:", error);
    }
  };

  const handleImport = async () => {
    if (!parsedRows.length || !companyId || !templateName) return;
    if (validationErrors.length > 0) return;

    try {
      const result = await importMutation.mutateAsync({
        rows: parsedRows,
        templateName,
        companyId,
      });

      if (result.success) {
        setImportResult({
          success: true,
          imported: result.imported,
          skipped: result.skipped,
        });
        // Clear the form
        setSelectedFile(null);
        setParsedRows([]);
        setValidationErrors([]);
      }
    } catch (error) {
      console.error("Import error:", error);
    }
  };

  const isFormValid =
    selectedFile && companyId && templateName && parsedRows.length > 0;
  const canImport = isFormValid && validationErrors.length === 0;

  return (
    <div className="container mx-auto max-h-full w-[80vw] space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Importar Mapa de Cuentas</h1>
          <p className="text-sm text-muted-foreground">
            Importa la configuración de mapeo de cuentas desde un archivo CSV
          </p>
        </div>
      </div>

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle>Configuración de Importación</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Company Selector */}
            <div className="space-y-2">
              <Label htmlFor="company">Empresa *</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger id="company">
                  <SelectValue placeholder="Selecciona una empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Template Name */}
            <div className="space-y-2">
              <Label htmlFor="template">Nombre de Plantilla *</Label>
              <Input
                id="template"
                placeholder="ej: DISEÑO_UNO"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Si la plantilla existe, se sobrescribirán los mapeos
              </p>
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="csv-file">Archivo CSV *</Label>
            <div className="flex items-center gap-2">
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="flex-1"
              />
              <Button
                onClick={handleParse}
                disabled={!selectedFile || parseCSVMutation.isPending}
              >
                {parseCSVMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    Analizar
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Formato esperado: CSV delimitado por punto y coma (;)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Errores de Validación</AlertTitle>
          <AlertDescription>
            <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
              {validationErrors.map((error, idx) => (
                <div key={idx} className="text-sm">
                  Fila {error.row}: {error.error}
                </div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Preview Table */}
      {parsedRows.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Vista Previa ({parsedRows.length} registros)
              </CardTitle>
              <div className="flex gap-2">
                {companyId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleValidate}
                    disabled={validateMutation.isPending}
                  >
                    {validateMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Validando...
                      </>
                    ) : (
                      "Validar"
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleImport}
                  disabled={!canImport || importMutation.isPending}
                >
                  {importMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Importar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead>Clase</TableHead>
                    <TableHead>SubClase</TableHead>
                    <TableHead>SubClase 2</TableHead>
                    <TableHead>Grupo Cuenta</TableHead>
                    <TableHead className="text-right">Signo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.slice(0, 50).map((row, idx) => (
                    <TableRow
                      key={idx}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      style={{ animationDelay: `${Math.min(idx * 40, 400)}ms` }}
                    >
                      <TableCell className="font-mono">{row.codigo}</TableCell>
                      <TableCell>{row.cuenta}</TableCell>
                      <TableCell>{row.clase}</TableCell>
                      <TableCell>{row.sub_clase}</TableCell>
                      <TableCell>{row.sub_clase_2}</TableCell>
                      <TableCell>{row.grupo_cuenta}</TableCell>
                      <TableCell className="text-right">{row.signo}</TableCell>
                    </TableRow>
                  ))}
                  {parsedRows.length > 50 && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-sm text-muted-foreground"
                      >
                        ... y {parsedRows.length - 50} registros más
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Success */}
      {importResult?.success && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Importación Exitosa</AlertTitle>
          <AlertDescription>
            Se importaron {importResult.imported} mapeos correctamente
            {importResult.skipped > 0 && ` (${importResult.skipped} omitidos)`}.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
